import { useState } from 'react';
import { Search } from 'lucide-react';
import { Tabs, TabsContent } from '@/ui/base/tabs';
import { Input } from '@/ui/base/input';
import { PageHeader } from '@/ui/PageHeader';
import { designSystemSections } from './sectionRegistry';
import { useBuscaNaDocumentacao } from './useBuscaNaDocumentacao';
import { SectionNav } from './SectionNav';
import { DesignSystemPdfActions } from './DesignSystemPdfActions';

export default function AdminDesignSystem() {
  const { consulta, definirConsulta, resultados, contagemPorSecao, rolarAte } =
    useBuscaNaDocumentacao();
  const [activeTab, setActiveTab] = useState<string>(designSystemSections[0]?.id ?? '');

  const abrirResultado = (secao: string, item: string) => {
    setActiveTab(secao);
    definirConsulta('');
    rolarAte(item);
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Design System"
        description="Documentação viva dos padrões visuais e componentes do Sistema TEG"
      >
        <DesignSystemPdfActions />
      </PageHeader>

      {/* Busca — procura nos itens do mesmo registro das seções (catalogo.ts) */}
      <div className="relative">
        <Search
          className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground"
          aria-hidden="true"
        />
        <Input
          type="search"
          aria-label="Buscar na documentação"
          placeholder="Buscar componentes, cores, tokens..."
          value={consulta}
          onChange={(e) => definirConsulta(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') definirConsulta('');
          }}
          className="pl-10"
        />
        {consulta && resultados.length > 0 && (
          <ul
            aria-label="Resultados da busca"
            className="absolute z-50 top-full mt-1 w-full rounded-xl border border-border bg-popover shadow-lg max-h-64 overflow-y-auto"
          >
            {resultados.map((r) => (
              <li key={r.id}>
                <button
                  type="button"
                  onClick={() => abrirResultado(r.secao, r.id)}
                  className="w-full flex items-center justify-between gap-3 px-4 py-2.5 text-sm hover:bg-accent/50 focus-visible:bg-accent/50 focus-visible:outline-hidden transition-colors text-left"
                >
                  <span className="text-foreground">{r.titulo}</span>
                  <span className="shrink-0 text-xs text-muted-foreground">{r.rotuloDaSecao}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
        {consulta && resultados.length === 0 && (
          <div className="absolute z-50 top-full mt-1 w-full rounded-xl border border-border bg-popover shadow-lg p-4 text-center text-sm text-muted-foreground">
            Nenhum resultado para "{consulta}". Tente o nome do componente ou do token.
          </div>
        )}
        <p className="sr-only" aria-live="polite">
          {consulta
            ? `${resultados.length} ${resultados.length === 1 ? 'resultado' : 'resultados'}`
            : ''}
        </p>
      </div>

      {/* Seções — mesma fonte usada pelo PDF */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <SectionNav
          sections={designSystemSections}
          value={activeTab}
          onValueChange={setActiveTab}
          counts={contagemPorSecao}
          showCounts={!!consulta}
        />

        {designSystemSections.map((s) => (
          <TabsContent key={s.id} value={s.id} className="mt-6 space-y-4">
            <p className="text-sm text-muted-foreground">{s.intro}</p>
            {s.render()}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
